import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import MediaActions from '../../store/actions/MediaAction';
import { MEDIA_OPTIONS, NOTIFICATION_BANNER_DETAILS, UPLOAD_STATUS_TEXT_MAP } from '../../constants/media.constants';
import { STORE_NAMES } from '../../constants/store.constants';
import MemoizedImageItem from './MemoizedImageItem';
import NotificationBanner from '../NotificationBanner/NotificationBanner';

const AddImages = ({ images, displayImageListServiceErrorNotification, setImageListServiceErrorStatus, isRefreshDisabled }) => {
  const dispatch = useDispatch();

  const [imageListToDisplay, setImageListToDisplay] = useState([]);
  const [validationErrorActive, setValidationErrorActive] = useState(false);

  const descriptionInputRefs = useRef({});

  const {
    imageIdDescriptionMap,
    selectedImages,
    imageUploadStatusMap,
    displayUploadErrorNotification,
    displayPartialErrorNotification,
    disableImageListInteraction,
    imageIdBeingEdited
  } = useSelector(state => state?.[STORE_NAMES.MEDIA]);

  const transformImageItem = (imageItem, index, overrideDescription = null) => {
    const descriptionFromStoreOrLocal = overrideDescription ?? imageIdDescriptionMap[imageItem.imageId] ?? imageItem?.description ?? '';
    const uploadStatusFromStoreOrLocal = imageUploadStatusMap[imageItem.imageId] || imageItem.uploadStatus;

    let descriptionTextToDisplay = '';
    if (descriptionFromStoreOrLocal.length > 0) {
      descriptionTextToDisplay = descriptionFromStoreOrLocal;
    } else if (uploadStatusFromStoreOrLocal !== UPLOAD_STATUS_TEXT_MAP.SUCCESS) {
      descriptionTextToDisplay = 'Add description';
    }

    return {
      ...imageItem,
      imageName: imageItem.imageName || `Image ${index + 1}`,
      description: descriptionFromStoreOrLocal,
      descriptionTitle: descriptionFromStoreOrLocal.length > 50 ? descriptionFromStoreOrLocal : '',
      descriptionTextToDisplay,
      deducedDescriptionClassName: descriptionFromStoreOrLocal.length ? 'vve-description-text-filled' : 'vve-description-text',
      uploadStatus: uploadStatusFromStoreOrLocal
    };
  };

  const handleImageEditClick = imageId => {
    dispatch(MediaActions.setImageIdBeingEdited(imageId));
  };

  const handleImageSelectionClick = imageId => {
    if (selectedImages.some(image => image.fileId === imageId)) {
      dispatch(MediaActions.removeSelectedImage(imageId));
    } else {
      const existingImage = imageListToDisplay.find(image => image.imageId === imageId);
      if (existingImage) {
        const { imageId: fileId, imageName: fileName, description } = existingImage;
        const payload = { fileId, fileName, description };
        dispatch(MediaActions.addSelectedImage(payload));
      }
    }
  };

  const handleDescriptionSave = (imageId, newDescription) => {
    const descriptionTextToSave =
      typeof newDescription === 'string' ? newDescription.trim() : (descriptionInputRefs.current[imageId]?.value || '').trim();

    dispatch(MediaActions.updateImageDescription(imageId, descriptionTextToSave));

    setImageListToDisplay(prevList =>
      prevList.map((item, index) => {
        if (item.imageId === imageId) {
          return transformImageItem(item, index, descriptionTextToSave);
        }
        return item;
      })
    );
  };

  useEffect(() => {
    setImageListToDisplay(prevList =>
      prevList.map((item, index) => {
        if (imageUploadStatusMap[item.imageId]) {
          return transformImageItem(item, index);
        }
        return item;
      })
    );
  }, [imageUploadStatusMap]);

  useEffect(() => {
    if (images && images.length > 0) {
      const transformed = images.map((item, index) => transformImageItem(item, index));
      setImageListToDisplay(transformed);
    }
  }, [images]);

  useEffect(() => {
    if (isRefreshDisabled) {
      setValidationErrorActive(false);
    }
  }, [isRefreshDisabled]);

  // Class for while fetching disables all (and opacity), otherwise only error disables pointer/cursor on items.
  const rootClass = ['vve-images', disableImageListInteraction ? 'vve-images-no-interaction' : ''].filter(Boolean).join(' ');

  const validationErrorBlockClass = validationErrorActive ? 'vve-images-validation-error-block' : '';

  return (
    <div className={rootClass}>
      {(displayImageListServiceErrorNotification || displayUploadErrorNotification) && (
        <NotificationBanner
          {...NOTIFICATION_BANNER_DETAILS.serviceError}
          onClose={() => {
            dispatch(MediaActions.setDisplayUploadServiceErrorNotification(false));
            setImageListServiceErrorStatus(false);
          }}
        />
      )}
      {displayPartialErrorNotification && (
        <NotificationBanner
          {...NOTIFICATION_BANNER_DETAILS.partialError}
          onClose={() => dispatch(MediaActions.setDisplayPartialErrorNotification(false))}
        />
      )}
      {imageListToDisplay.length > 0 ? (
        imageListToDisplay.map((item, index) => (
          <MemoizedImageItem
            key={item.imageId}
            imageId={item.imageId}
            previewImgSasUrl={item.previewImgSasUrl}
            imageName={item.imageName}
            description={item.description}
            uploadStatus={item.uploadStatus}
            descriptionTitle={item.descriptionTitle}
            descriptionTextToDisplay={item.descriptionTextToDisplay}
            deducedDescriptionClassName={item.deducedDescriptionClassName}
            handleDescriptionSave={handleDescriptionSave}
            descriptionInputRefs={descriptionInputRefs}
            handleImageSelectionClick={handleImageSelectionClick}
            isSelected={selectedImages.some(image => image.fileId === item.imageId)}
            handleImageEditClick={handleImageEditClick}
            imageIdBeingEdited={imageIdBeingEdited}
            validationErrorActive={validationErrorActive}
            setValidationErrorActive={setValidationErrorActive}
            validationErrorBlockClass={validationErrorBlockClass}
            isRefreshDisabled={isRefreshDisabled}
          />
        ))
      ) : (
        <p className="vve-noimages-text">{MEDIA_OPTIONS.noImagesText}</p>
      )}
    </div>
  );
};

AddImages.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      imageId: PropTypes.string.isRequired,
      previewImgSasUrl: PropTypes.string.isRequired,
      imageName: PropTypes.string,
      description: PropTypes.string,
      uploadStatus: PropTypes.string
    })
  ).isRequired,
  displayImageListServiceErrorNotification: PropTypes.bool.isRequired,
  setImageListServiceErrorStatus: PropTypes.func.isRequired,
  isRefreshDisabled: PropTypes.bool.isRequired
};

export default AddImages;
